/**
 * Animation System - Simpler integration for Flowi
 * This provides a more direct integration path with the existing codebase
 */

import { ViewItem, Connector } from 'src/types';

export interface Animation {
    id: string;
    targetId: string;
    targetType: 'viewItem' | 'connector';
    startTime: number;
    duration: number;
    properties: Record<string, { from: number; to: number; current: number }>;
    easing: (t: number) => number;
    onUpdate?: () => void;
    onComplete?: () => void;
}

export class AnimationSystem {
    private animations: Map<string, Animation> = new Map();
    private animationId = 0;
    private rafId: number | null = null;
    private lastTime = 0;

    /**
     * Animate a ViewItem property
     */
    animate(
        target: ViewItem | Connector | any,
        property: string,
        to: number,
        options: {
            duration?: number;
            easing?: (t: number) => number;
            onUpdate?: () => void;
            onComplete?: () => void;
        } = {}
    ): string {
        const {
            duration = 1000,
            easing = AnimationSystem.easings.easeOutQuad,
            onUpdate,
            onComplete
        } = options;

        console.log(this.lastTime);
        const animId = `anim_${this.animationId++}`;
        const from = this.getPropertyValue(target, property);

        const animation: Animation = {
            id: animId,
            targetId: (target as any).id || animId,
            targetType: 'viewItem',
            startTime: performance.now(),
            duration,
            properties: {
                [property]: { from, to, current: from }
            },
            easing,
            onUpdate,
            onComplete
        };

        this.animations.set(animId, animation);
        this.startAnimationLoop();

        return animId;
    }

    /**
     * Animate multiple properties
     */
    animateMultiple(
        target: any,
        properties: Record<string, number>,
        options: Parameters<typeof this.animate>[3] = {}
    ): string[] {
        return Object.entries(properties).map(([prop, value]) =>
            this.animate(target, prop, value, options)
        );
    }

    /**
     * Stop an animation
     */
    stop(animationId: string, jumpToEnd = false): void {
        const animation = this.animations.get(animationId);
        if (!animation) return;

        if (jumpToEnd) {
            // Set final values
            Object.entries(animation.properties).forEach(([prop, values]) => {
                this.setPropertyValue(
                    this.getTargetById(animation.targetId),
                    prop,
                    values.to
                );
            });
        }

        this.animations.delete(animationId);
        this.checkAnimationLoop();
    }

    /**
     * Stop all animations
     */
    stopAll(jumpToEnd = false): void {
        const ids = Array.from(this.animations.keys());
        ids.forEach(id => this.stop(id, jumpToEnd));
    }

    /**
     * Check if animating
     */
    isAnimating(): boolean {
        return this.animations.size > 0;
    }

    // Private methods

    private startAnimationLoop(): void {
        if (this.rafId !== null) return;

        const animate = (currentTime: number) => {
            // const deltaTime = currentTime - this.lastTime;
            this.lastTime = currentTime;

            this.updateAnimations(currentTime);

            if (this.animations.size > 0) {
                this.rafId = requestAnimationFrame(animate);
            } else {
                this.stopAnimationLoop();
            }
        };

        this.lastTime = performance.now();
        this.rafId = requestAnimationFrame(animate);
    }

    private stopAnimationLoop(): void {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    private checkAnimationLoop(): void {
        if (this.animations.size === 0) {
            this.stopAnimationLoop();
        }
    }

    private updateAnimations(currentTime: number): void {
        const completed: string[] = [];

        this.animations.forEach((animation, id) => {
            const elapsed = currentTime - animation.startTime;
            const progress = Math.min(elapsed / animation.duration, 1);
            const easedProgress = animation.easing(progress);

            // Update each property
            Object.entries(animation.properties).forEach(([prop, values]) => {
                const newValue = values.from + (values.to - values.from) * easedProgress;
                values.current = newValue;

                const target = this.getTargetById(animation.targetId);
                if (target) {
                    this.setPropertyValue(target, prop, newValue);
                }
            });

            if (animation.onUpdate) {
                animation.onUpdate();
            }

            if (progress >= 1) {
                completed.push(id);
                if (animation.onComplete) {
                    animation.onComplete();
                }
            }
        });

        // Remove completed animations
        completed.forEach(id => this.animations.delete(id));
    }

    private getPropertyValue(target: any, property: string): number {
        const parts = property.split('.');
        let value = target;

        for (const part of parts) {
            value = value?.[part];
        }

        return typeof value === 'number' ? value : 0;
    }

    private setPropertyValue(target: any, property: string, value: number): void {
        const parts = property.split('.');
        const lastPart = parts.pop()!;

        let obj = target;
        for (const part of parts) {
            if (!obj[part]) obj[part] = {};
            obj = obj[part];
        }

        obj[lastPart] = value;
    }

    private getTargetById(id: string): any {
        if (id) {
            // Lookup logic to find the target by ID would go here
            // For example, searching in a global store or context
        }
        // Animation system is not fully integrated yet with the store
        // The target reference needs to be passed directly to the animate() method
        // instead of looking it up by ID
        // For now, animations work by directly updating the passed target object
        return null;
    }

    /**
     * Common easing functions
     */
    static easings = {
        linear: (t: number) => t,
        easeInQuad: (t: number) => t * t,
        easeOutQuad: (t: number) => t * (2 - t),
        easeInOutQuad: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
        easeInCubic: (t: number) => t * t * t,
        easeOutCubic: (t: number) => (--t) * t * t + 1,
        easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
        easeOutElastic: (t: number) => {
            const c4 = (2 * Math.PI) / 3;
            return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
        },
        easeOutBounce: (t: number) => {
            const n1 = 7.5625;
            const d1 = 2.75;
            if (t < 1 / d1) {
                return n1 * t * t;
            } else if (t < 2 / d1) {
                return n1 * (t -= 1.5 / d1) * t + 0.75;
            } else if (t < 2.5 / d1) {
                return n1 * (t -= 2.25 / d1) * t + 0.9375;
            } else {
                return n1 * (t -= 2.625 / d1) * t + 0.984375;
            }
        }
    };
}

// Global instance
export const animationSystem = new AnimationSystem();